import { Injectable } from '@angular/core';
import type { ILaneConfig, LaneConfig } from 'commons';
import type { MarkAreaComponentOption, XAXisComponentOption } from 'echarts';
import type { TestPhaseItem } from '../../../app/states/test-phase';
import { buildLabeledTimeRangeMarkArea } from './mark-area-builder.util';

/**
 * State-based color palette for test phases
 */
const TEST_PHASE_STATE_COLORS = {
  approved: {
    background: 'rgba(241, 246, 232)',
    border: '#A1C861',
    label: '#3C7029',
  },
  edited: {
    background: 'rgba(254, 247, 221)',
    border: '#E9B300',
    label: '#814E00',
  },
  neutral: {
    background: 'rgba(232, 232, 232)',
    border: '#d0d0d0',
    label: '#444444',
  },
} as const;

/**
 * Interval representation for a single test phase (used for tooltips)
 */
export interface TestPhaseInterval {
  readonly startTime: Date;
  readonly endTime: Date;
  readonly phaseName: string;
  /** State-based border/outline color */
  readonly borderColor: string;
  /** State-based background fill color (rgba with alpha) */
  readonly backgroundColor: string;
  /** State-based label/text color */
  readonly labelColor: string;
  readonly identifier: string;
}

/**
 * Result of test phase mark area generation
 */
export interface TestPhaseAnalysisResult {
  readonly markArea: MarkAreaComponentOption;
  readonly laneIndex: number;
  readonly xAxisIndex: number;
  readonly hasData: boolean;
  readonly intervals: TestPhaseInterval[];
}

/**
 * Service responsible for building test phase mark areas for ECharts multilane charts.
 * Uses API-sourced TestPhaseItem data to create colored mark areas.
 */
@Injectable({ providedIn: 'root' })
export class TestPhaseMarkAreaBuilderService {
  /**
   * Builds test phase mark areas for all lanes from API-sourced phase data.
   * Each lane gets its own set of mark area overlays based on the visible phases.
   *
   * @param lanes - Array of lane configurations (used for xAxis mapping)
   * @param xAxis - X-axis configurations for axis index mapping
   * @param phases - Test phase items from the NGXS store (API-sourced)
   * @returns Array of test phase analysis results, one per lane
   */
  buildTestPhaseMarkAreas(lanes: LaneConfig[] | ILaneConfig[], xAxis: XAXisComponentOption[], phases: TestPhaseItem[]): TestPhaseAnalysisResult[] {
    const visiblePhases = phases.filter((p) => p.visible);

    if (visiblePhases.length === 0) {
      return [];
    }

    const results: TestPhaseAnalysisResult[] = [];

    // Apply mark areas to every lane so phases span the full chart
    for (let laneIndex = 0; laneIndex < lanes.length; laneIndex += 1) {
      const xAxisIndex = this.findXAxisIndex(xAxis, laneIndex);
      const intervals = this.phasesToIntervals(visiblePhases);
      const markArea = buildLabeledTimeRangeMarkArea(intervals);

      results.push({
        markArea,
        laneIndex,
        xAxisIndex,
        hasData: intervals.length > 0,
        intervals,
      });
    }

    return results;
  }

  /**
   * Returns state-based colors for a test phase.
   * Priority: approved > edited > neutral (created & not approved)
   */
  private getPhaseStateColors(phase: TestPhaseItem): (typeof TEST_PHASE_STATE_COLORS)[keyof typeof TEST_PHASE_STATE_COLORS] {
    if (phase.isApproved) {
      return TEST_PHASE_STATE_COLORS.approved;
    }
    if (phase.updatedBy !== phase.createdBy) {
      return TEST_PHASE_STATE_COLORS.edited;
    }
    return TEST_PHASE_STATE_COLORS.neutral;
  }

  /**
   * Converts TestPhaseItem array into TestPhaseInterval array for rendering.
   */
  private phasesToIntervals(phases: TestPhaseItem[]): TestPhaseInterval[] {
    return phases.map((phase) => {
      const stateColors = this.getPhaseStateColors(phase);
      return {
        startTime: new Date(phase.startDate),
        endTime: new Date(phase.endDate),
        phaseName: phase.phaseName,
        borderColor: stateColors.border,
        backgroundColor: stateColors.background,
        labelColor: stateColors.label,
        identifier: phase.identifier,
      };
    });
  }

  /**
   * Finds the X-axis index for a given lane.
   */
  private findXAxisIndex(xAxis: XAXisComponentOption[], laneIndex: number): number {
    const index = xAxis.findIndex(({ id }) => id === `LaneX-${laneIndex}`);
    return Math.max(index, 0);
  }
}
